import { motion, useReducedMotion } from "motion/react";

/**
 * Global cinematic quote section rendered above the site footer on every
 * route that uses the shared AppShell layout. Purely visual — no business
 * logic, data fetching or interactive elements.
 *
 * Theme-aware: the section uses the site’s existing light/dark tokens and
 * switches automatically with the global theme.
 */
export function GlobalQuoteSection() {
  const reduced = useReducedMotion();

  const content = (
    <>
      <div
        className="mb-3 text-3xl leading-none text-muted-foreground/30 dark:text-white/10 sm:text-4xl"
        aria-hidden="true"
      >
        “
      </div>
      <blockquote className="max-w-3xl">
        <p className="font-brand-serif text-[clamp(1.5rem,5.2vw,3.5rem)] font-medium leading-[1.22] tracking-tight text-foreground dark:text-white/95">
          <span className="block">You don&apos;t need more time.</span>
          <span className="block">You need a better way to use it.</span>
        </p>
      </blockquote>
      <div className="quote-line mx-auto mt-8 h-px w-28 sm:mt-10 sm:w-36" />
      <p className="mt-5 text-[10px] font-semibold uppercase tracking-[0.32em] text-muted-foreground dark:text-white/45 sm:text-[11px]">
        THE QUANTUM MINDS
      </p>
    </>
  );

  return (
    <section className="quote-section relative w-full overflow-hidden">
      <div
        className="pointer-events-none absolute inset-0 overflow-hidden"
        aria-hidden="true"
      >
        <div className="quote-glow absolute left-1/2 top-1/2 h-[140%] w-[140%] -translate-x-1/2 -translate-y-1/2" />
      </div>

      {reduced ? (
        <div className="relative z-10 mx-auto flex max-w-4xl flex-col items-center px-5 py-20 text-center sm:py-24 md:py-28 lg:py-32">
          {content}
        </div>
      ) : (
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="relative z-10 mx-auto flex max-w-4xl flex-col items-center px-5 py-20 text-center sm:py-24 md:py-28 lg:py-32"
        >
          {content}
        </motion.div>
      )}

      <style>{`
        .quote-section {
          background-color: var(--background);
          color: var(--foreground);
          --line-center: oklch(0.62 0.2 262 / 0.45);
          --line-accent: oklch(0.6 0.22 300 / 0.45);
          --glow-core: oklch(0.62 0.2 262 / 0.14);
          --glow-mid: oklch(0.6 0.22 300 / 0.10);
        }
        .dark .quote-section {
          background-color: oklch(0.045 0 0);
          color: oklch(0.97 0.005 265 / 0.95);
          --line-center: oklch(0.62 0.2 262 / 0.65);
          --line-accent: oklch(0.6 0.22 300 / 0.65);
          --glow-core: oklch(0.62 0.2 262 / 0.22);
          --glow-mid: oklch(0.6 0.22 300 / 0.14);
        }
        .quote-line {
          background: linear-gradient(
            90deg,
            transparent,
            var(--line-center),
            var(--line-accent),
            transparent
          );
        }
        .quote-glow {
          background: radial-gradient(
            ellipse at center,
            var(--glow-core) 0%,
            var(--glow-mid) 38%,
            transparent 72%
          );
          filter: blur(72px);
          animation: quoteGlowDrift 16s ease-in-out infinite;
        }
        @keyframes quoteGlowDrift {
          0%,
          100% {
            transform: translate(-50%, -50%) scale(1);
            opacity: 0.55;
          }
          50% {
            transform: translate(-48%, -52%) scale(1.1);
            opacity: 0.78;
          }
        }
        @media (prefers-reduced-motion: reduce) {
          .quote-glow {
            animation: none;
          }
        }
      `}</style>
    </section>
  );
}
